import React, { FC, useState } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { lpResponsiveStyles } from "./constants";
import { InView } from "react-intersection-observer";
import useTheme from "@material-ui/core/styles/useTheme";
import { useMediaQuery } from "@material-ui/core";
import {
  LazyLoadComponent,
  LazyLoadImage,
} from "react-lazy-load-image-component";

const useStyles = makeStyles((theme) => {
  const summaryResponsive = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    top: `calc(${baseWidth} * 2.514666667 * 0.675)`,
    padding: "0 10%",
    width: "100%",
  }));
  const summaryReverseResponsive = lpResponsiveStyles(theme, (baseWidth) => ({
    position: "absolute",
    top: `calc(${baseWidth} * 2.514666667 * 0.575)`,
    "& > div": {
      height: `calc(${baseWidth} * 1.045)`,
    },
  }));
  const videoPlaceholder = lpResponsiveStyles(theme, (baseWidth) => ({
    height: `calc(${baseWidth} * 2.493333333)`,
  }));
  return {
    "@keyframes fadeIn": {
      "0%": {
        opacity: 0,
      },
      "100%": {
        opacity: 1,
      },
    },
    spHeroImage: {
      width: "100%",
    },
    root: {
      position: "relative",
    },
    summary: {
      animation: "$fadeIn ease 1s",
      ...summaryResponsive,
    },
    summaryReverse: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      animation: "$fadeIn ease 1s",
      ...summaryReverseResponsive,
    },
    videoPlaceholder,
  };
});

type Props = {
  contentRoot: Element;
  onAnimationStopped?: () => void;
  onViewSummary: (inView: boolean) => void;
  onViewKeyVisual?: () => void;
  onViewOutKeyVisual?: () => void;
};
export const SpMainImages: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSp = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRoot = isSp ? null : props.contentRoot;
  const [showSummary, setShowSummary] = useState(false);
  return (
    <div className={classes.root}>
      <InView
        root={contentRoot}
        rootMargin={"-60% 0px -30% 0px"}
        threshold={0}
        onChange={(inView, entry) => {
          if (inView) {
            props.onViewKeyVisual && props.onViewKeyVisual();
          }
        }}
      >
        <video
          autoPlay={true}
          playsInline={true}
          muted
          style={{ width: "100%" }}
          onEnded={() => {
            setShowSummary(true);
            props.onAnimationStopped && props.onAnimationStopped();
          }}
          controls={false}
          disablePictureInPicture
          controlsList="nodownload"
        >
          <source type="video/mp4" src="/img/lp/obverse-key.mp4" />
        </video>
      </InView>
      <InView
        root={contentRoot}
        rootMargin={"-50% 0px -40% 0px"}
        threshold={0}
        onChange={(inView, entry) => {
          if (inView) {
            props.onViewSummary(inView);
          }
        }}
        className={classes.summary}
        style={{ display: showSummary ? "" : "none" }}
      >
        <LazyLoadImage
          useIntersectionObserver
          src="/img/lp/summary.png"
          width="100%"
          alt="生徒会役員共クラウドファンディングの概要"
        />
      </InView>
    </div>
  );
};

export const SpMainImagesReverse: FC<Props> = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSp = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <div className={classes.root}>
      <div>
        <LazyLoadComponent
          placeholder={<div className={classes.videoPlaceholder} />}
        >
          <video
            autoPlay={true}
            playsInline={true}
            muted
            style={{ width: "100%" }}
            controls={false}
            disablePictureInPicture
            controlsList="nodownload"
          >
            <source type="video/mp4" src="/img/lp/reverse-key.mp4" />
          </video>
        </LazyLoadComponent>
      </div>
      <InView
        root={isSp ? null : props.contentRoot}
        rootMargin={"-80% 0px -10% 0px"}
        threshold={0}
        onChange={(inView, entry) => {
          props.onViewSummary(inView);
        }}
        className={classes.summaryReverse}
      >
        <div
          style={{
            margin: "0 calc(7% * -1)",
          }}
        >
          <LazyLoadImage
            useIntersectionObserver
            src="/img/lp/summary_reverse.png"
            width="96%"
            alt="生徒会役員共クラウドファンディングの概要(裏)"
          />
        </div>
      </InView>
    </div>
  );
};
